import axios from 'axios';

// Use the current origin so the API call is same-origin on ANY host (custom
// domain, preview, deployed emergent.host, local dev). This avoids cross-site
// cookies/CORS issues entirely. Falls back to REACT_APP_BACKEND_URL only when
// window is unavailable (SSR — not used here, but keeps the constant safe).
const ORIGIN = typeof window !== 'undefined' && window.location?.origin
  ? window.location.origin
  : process.env.REACT_APP_BACKEND_URL;
const API = `${ORIGIN}/api`;

// Create axios instance with credentials
const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Contacts
export const contactsApi = {
  getAll: (params) => api.get('/contacts', { params }),
  get: (id) => api.get(`/contacts/${id}`),
  create: (data) => api.post('/contacts', data),
  update: (id, data) => api.put(`/contacts/${id}`, data),
  delete: (id) => api.delete(`/contacts/${id}`),
};

// Companies
export const companiesApi = {
  getAll: (params) => api.get('/companies', { params }),
  get: (id) => api.get(`/companies/${id}`),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id) => api.delete(`/companies/${id}`),
};

export const authApi = {
  me: () => api.get('/auth/me'),
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
};

export const passwordResetAdminApi = {
  list: (status) => api.get('/admin/password-requests', { params: status ? { status } : {} }),
  approve: (id) => api.post(`/admin/password-requests/${id}/approve`),
  delete: (id) => api.delete(`/admin/password-requests/${id}`),
};

// Demo requests (pricing page)
export const demoRequestsApi = {
  create: (data) => api.post('/demo-requests', data),
  list: (status) => api.get('/admin/demo-requests', { params: status ? { status } : {} }),
  update: (id, data) => api.patch(`/admin/demo-requests/${id}`, data),
  delete: (id) => api.delete(`/admin/demo-requests/${id}`),
};

// Stripe billing (pricing page upgrades)
export const billingApi = {
  checkoutSession: (packageId, originUrl, email) =>
    api.post('/billing/checkout-session', {
      package_id: packageId,
      origin_url: originUrl,
      ...(email ? { email } : {}),
    }),
  status: (sessionId) => api.get(`/billing/status/${sessionId}`),
};

// CMS — admin-edited marketing content (case_study | partner | event | resource)
export const cmsApi = {
  listPublic: (type) => api.get('/cms/items', { params: type ? { type } : {} }),
  list:       (type) => api.get('/admin/cms/items', { params: type ? { type } : {} }),
  create:     (payload) => api.post('/admin/cms/items', payload),
  update:     (id, patch) => api.patch(`/admin/cms/items/${id}`, patch),
  delete:     (id) => api.delete(`/admin/cms/items/${id}`),
};

// Support — enquiries + surveys + chatbot
export const supportApi = {
  createEnquiry:  (data) => api.post('/support-enquiries', data),
  listEnquiries:  (status) => api.get('/admin/support-enquiries', { params: status ? { status } : {} }),
  updateEnquiry:  (id, patch) => api.patch(`/admin/support-enquiries/${id}`, patch),
  deleteEnquiry:  (id) => api.delete(`/admin/support-enquiries/${id}`),
  submitSurvey:   (data) => api.post('/surveys', data),
  listSurveys:    (type) => api.get('/admin/surveys', { params: type ? { type } : {} }),
  chat:           (data) => api.post('/support/chat', data),
  listChatSessions: ()   => api.get('/admin/support/chat-sessions'),
  getChatSession: (id)   => api.get(`/admin/support/chat-sessions/${id}`),
};

// Schedule & Remind — free tool for every signed-in user
export const scheduleApi = {
  list:   (scope = 'upcoming') => api.get('/schedule/events', { params: { scope } }),
  create: (data) => api.post('/schedule/events', data),
  update: (id, patch) => api.patch(`/schedule/events/${id}`, patch),
  delete: (id) => api.delete(`/schedule/events/${id}`),
};

// Finance
export const financeApi = {
  accounts: {
    list: () => api.get('/finance/accounts'),
    create: (d) => api.post('/finance/accounts', d),
    delete: (id) => api.delete(`/finance/accounts/${id}`),
  },
  journals: {
    list: () => api.get('/finance/journals'),
    create: (d) => api.post('/finance/journals', d),
  },
  invoices: {
    list: (params) => api.get('/finance/invoices', { params }),
    create: (d) => api.post('/finance/invoices', d),
    setStatus: (id, status) => api.patch(`/finance/invoices/${id}/status`, { status }),
    delete: (id) => api.delete(`/finance/invoices/${id}`),
  },
  expenses: {
    list: (params) => api.get('/finance/expenses', { params }),
    create: (d) => api.post('/finance/expenses', d),
    setStatus: (id, status) => api.patch(`/finance/expenses/${id}/status`, { status }),
    aiCategorize: (d) => api.post('/finance/expenses/ai-categorize', d),
  },
  budgets: {
    list: () => api.get('/finance/budgets'),
    create: (d) => api.post('/finance/budgets', d),
    delete: (id) => api.delete(`/finance/budgets/${id}`),
  },
  salesOrders: {
    list: (params) => api.get('/finance/sales-orders', { params }),
    create: (d) => api.post('/finance/sales-orders', d),
    setStatus: (id, status) => api.patch(`/finance/sales-orders/${id}/status`, { status }),
  },
  summary: () => api.get('/finance/summary'),
};

// HR
export const hrApi = {
  employees: {
    list: (params) => api.get('/hr/employees', { params }),
    create: (d) => api.post('/hr/employees', d),
    update: (id, d) => api.put(`/hr/employees/${id}`, d),
    delete: (id) => api.delete(`/hr/employees/${id}`),
  },
  leaveRequests: {
    list: (params) => api.get('/hr/leave-requests', { params }),
    create: (d) => api.post('/hr/leave-requests', d),
    setStatus: (id, status) => api.patch(`/hr/leave-requests/${id}/status`, { status }),
    aiSummary: (id) => api.post(`/hr/leave-requests/${id}/ai-summary`),
    balance: (employeeId) => api.get(`/hr/leave-balances/${employeeId}`),
  },
  reviews: {
    list: (params) => api.get('/hr/reviews', { params }),
    create: (d) => api.post('/hr/reviews', d),
    update: (id, d) => api.put(`/hr/reviews/${id}`, d),
  },
  payroll: {
    list: () => api.get('/hr/payroll'),
    create: (d) => api.post('/hr/payroll', d),
    payslips: (runId) => api.get(`/hr/payroll/${runId}/payslips`),
  },
  summary: () => api.get('/hr/summary'),
};

// Teams
export const teamsApi = {
  list: () => api.get('/teams'),
  create: (d) => api.post('/teams', d),
  update: (id, d) => api.put(`/teams/${id}`, d),
  delete: (id) => api.delete(`/teams/${id}`),
};

// Invest (business marketplace + bids + portfolio)
export const investApi = {
  summary: () => api.get('/invest/summary'),
  listings: {
    list:   (params) => api.get('/business-listings', { params }),
    get:    (id) => api.get(`/business-listings/${id}`),
    create: (d) => api.post('/business-listings', d),
    update: (id, d) => api.put(`/business-listings/${id}`, d),
    delete: (id) => api.delete(`/business-listings/${id}`),
    inquire: (id, d) => api.post(`/business-listings/${id}/inquire`, d),
    inquiries: (id) => api.get(`/business-listings/${id}/inquiries`),
    bids: (id) => api.get(`/business-listings/${id}/bids`),
    placeBid: (id, d) => api.post(`/business-listings/${id}/bids`, d),
  },
  myBids: (params) => api.get('/invest/my-bids', { params }),
  receivedBids: (params) => api.get('/invest/received-bids', { params }),
  setBidStatus: (id, status) => api.patch(`/invest/bids/${id}/status`, { status }),
  portfolio: () => api.get('/invest/portfolio'),
};

// Deals
export const dealsApi = {
  getAll: (params) => api.get('/deals', { params }),
  get: (id) => api.get(`/deals/${id}`),
  create: (data) => api.post('/deals', data),
  update: (id, data) => api.put(`/deals/${id}`, data),
  updateStage: (id, stage) => api.patch(`/deals/${id}/stage`, { stage }),
  delete: (id) => api.delete(`/deals/${id}`),
  readiness: (id) => api.get(`/deals/${id}/readiness`),
};

// Tasks
export const tasksApi = {
  getAll: (params) => api.get('/tasks', { params }),
  get: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
};

// Documents
export const documentsApi = {
  getAll: (params) => api.get('/documents', { params }),
  upload: (formData, params) => api.post('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    params
  }),
  download: (id) => api.get(`/documents/${id}/download`, { responseType: 'blob' }),
  preview: (id) => api.get(`/documents/${id}/preview`, { responseType: 'blob' }),
  previewUrl: (id, token) => `${API}/documents/${id}/preview${token ? `?auth=${token}` : ''}`,
  rename: (id, name) => api.patch(`/documents/${id}/rename`, { name }),
  move: (id, folder_id) => api.patch(`/documents/${id}/move`, { folder_id }),
  delete: (id) => api.delete(`/documents/${id}`),
};

// Folders
export const foldersApi = {
  getAll: (params) => api.get('/folders', { params }),
  get: (id) => api.get(`/folders/${id}`),
  create: (data) => api.post('/folders', data),
  createFromTemplate: (data) => api.post('/folders/from-template', data),
  rename: (id, name) => api.put(`/folders/${id}`, { name }),
  move: (id, parent_id) => api.patch(`/folders/${id}/move`, { parent_id }),
  download: (id) => api.get(`/folders/${id}/download`, { responseType: 'blob' }),
  delete: (id) => api.delete(`/folders/${id}`),
};

// Folder Templates
export const folderTemplatesApi = {
  getAll: () => api.get('/folder-templates'),
  get: (id) => api.get(`/folder-templates/${id}`),
};

// Share Links
export const shareLinksApi = {
  getAll: () => api.get('/share-links'),
  create: (data) => api.post('/share-links', data),
  revoke: (id) => api.delete(`/share-links/${id}`),
  // Public (no auth)
  view: (token) => api.get(`/share/view/${token}`),
  downloadUrl: (token, docId) => `${API}/share/view/${token}/download/${docId}`,
  previewUrl: (token, docId) => `${API}/share/view/${token}/preview/${docId}`,
};

// Data Rooms
export const dataRoomsApi = {
  getAll: (params) => api.get('/data-rooms', { params }),
  get: (id) => api.get(`/data-rooms/${id}`),
  create: (data) => api.post('/data-rooms', data),
  delete: (id) => api.delete(`/data-rooms/${id}`),
};

// Procurement
export const procurementApi = {
  getAll: (params) => api.get('/procurement', { params }),
  get: (id) => api.get(`/procurement/${id}`),
  create: (data) => api.post('/procurement', data),
  update: (id, data) => api.put(`/procurement/${id}`, data),
  delete: (id) => api.delete(`/procurement/${id}`),
};

// Workflows
export const workflowsApi = {
  getAll: (params) => api.get('/workflows', { params }),
  get: (id) => api.get(`/workflows/${id}`),
  create: (data) => api.post('/workflows', data),
  update: (id, data) => api.put(`/workflows/${id}`, data),
  delete: (id) => api.delete(`/workflows/${id}`),
};

// Activities
export const activitiesApi = {
  getAll: (params) => api.get('/activities', { params }),
  create: (data) => api.post('/activities', data),
};

// Dashboard
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
};

// AI
export const aiApi = {
  getInsights: (data) => api.post('/ai/insights', data),
};

// Users
export const usersApi = {
  getAll: (params) => api.get('/users', { params }),
  updateRole: (id, role) => api.put(`/users/${id}/role`, { role }),
};

// CSV Import
export const importApi = {
  importContacts: (formData) => api.post('/contacts/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  importCompanies: (formData) => api.post('/companies/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// Reports
export const reportsApi = {
  getSummary: () => api.get('/reports/summary'),
  exportDeals: (format) => api.get(`/reports/export/deals`, { params: { format }, responseType: 'blob' }),
  exportContacts: (format) => api.get(`/reports/export/contacts`, { params: { format }, responseType: 'blob' }),
  exportCompanies: (format) => api.get(`/reports/export/companies`, { params: { format }, responseType: 'blob' }),
  exportProcurement: (format) => api.get(`/reports/export/procurement`, { params: { format }, responseType: 'blob' }),
};

// Notifications
export const notificationsApi = {
  getAll: (params) => api.get('/notifications', { params }),
  create: (data) => api.post('/notifications', data),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.patch('/notifications/read-all'),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// Workflow Execution
export const workflowExecutionApi = {
  execute: (id, triggerData) => api.post(`/workflows/${id}/execute`, { trigger_data: triggerData }),
  getExecutions: (id, params) => api.get(`/workflows/${id}/executions`, { params }),
};

// AI Chat
export const chatApi = {
  send: (message, sessionId) => api.post('/chat', { message, session_id: sessionId }),
  getHistory: (params) => api.get('/chat/history', { params }),
  getSessions: () => api.get('/chat/sessions'),
  deleteSession: (sessionId) => api.delete(`/chat/sessions/${sessionId}`),
};

// Business Intelligence
export const biApi = {
  getBusinessUnits: (params) => api.get('/business-units', { params }),
  getBusinessUnit: (id) => api.get(`/business-units/${id}`),
  createBusinessUnit: (data) => api.post('/business-units', data),
  updateBusinessUnit: (id, data) => api.put(`/business-units/${id}`, data),
  deleteBusinessUnit: (id) => api.delete(`/business-units/${id}`),
  getRevenueForecast: () => api.get('/bi/revenue-forecast'),
  getClientAcquisition: () => api.get('/bi/client-acquisition'),
};

// Business Listings (Build, Buy, Sell)
export const listingsApi = {
  getAll: (params) => api.get('/business-listings', { params }),
  get: (id) => api.get(`/business-listings/${id}`),
  create: (data) => api.post('/business-listings', data),
  update: (id, data) => api.put(`/business-listings/${id}`, data),
  delete: (id) => api.delete(`/business-listings/${id}`),
  inquire: (id, message) => api.post(`/business-listings/${id}/inquire`, { message }),
  getInquiries: (id) => api.get(`/business-listings/${id}/inquiries`),
  estimateValuation: (data) => api.post('/business-listings/estimate-valuation', data),
};

// Calendar
export const calendarApi = {
  getEvents: (params) => api.get('/calendar/events', { params }),
};

// Custom Fields
export const customFieldsApi = {
  getAll: (params) => api.get('/custom-fields', { params }),
  create: (data) => api.post('/custom-fields', data),
  delete: (id) => api.delete(`/custom-fields/${id}`),
  getTemplates: (sector) => api.get(`/custom-fields/templates/${sector}`),
  applyTemplate: (sector, entity) => api.post(`/custom-fields/templates/${sector}/apply`, { entity }),
  updateValues: (entity, entityId, values) => api.put(`/custom-field-values/${entity}/${entityId}`, { values }),
};

// Enhanced BI
export const enhancedBiApi = {
  getCrossSell: () => api.get('/bi/cross-sell-opportunities'),
  getCompetitive: () => api.get('/bi/competitive-analysis'),
  getAiCrossSell: (companyId) => api.post('/bi/ai-cross-sell', { company_id: companyId }),
};

// Audit Logs
export const auditApi = {
  getLogs: (params) => api.get('/audit-logs', { params }),
  getStats: () => api.get('/audit-logs/stats'),
  exportLogs: (params) => api.get('/audit-logs/export', { params, responseType: 'blob' }),
  getGdprLog: (userId) => api.get(`/audit-logs/gdpr/${userId}`),
};

// Affiliate Marketing
export const affiliateApi = {
  getProfile: () => api.get('/affiliate/profile'),
  updateProfile: (data) => api.put('/affiliate/profile', data),
  getReferrals: (params) => api.get('/affiliate/referrals', { params }),
  createReferral: (data) => api.post('/affiliate/referrals', data),
  updateReferralStatus: (id, status) => api.patch(`/affiliate/referrals/${id}/status`, { status }),
  getCommissions: (params) => api.get('/affiliate/commissions', { params }),
  getDashboard: () => api.get('/affiliate/dashboard'),
  getAllAffiliates: () => api.get('/affiliate/all'),
  getAllReferrals: (params) => api.get('/affiliate/all-referrals', { params }),
};

// User Preferences
export const preferencesApi = {
  get: () => api.get('/users/me/preferences'),
  updateLanguage: (language) => api.put('/users/me/language', { language }),
};

// Webhooks
export const webhooksApi = {
  getAll: () => api.get('/webhooks'),
  create: (data) => api.post('/webhooks', data),
  update: (id, data) => api.put(`/webhooks/${id}`, data),
  delete: (id) => api.delete(`/webhooks/${id}`),
  getEvents: () => api.get('/webhooks/events'),
  getLogs: (id, params) => api.get(`/webhooks/${id}/logs`, { params }),
  test: (id) => api.post(`/webhooks/test/${id}`),
  getIncoming: (params) => api.get('/webhooks/incoming', { params }),
};

// RBAC
export const rbacApi = {
  getPermissionSets: () => api.get('/rbac/permission-sets'),
  createPermissionSet: (data) => api.post('/rbac/permission-sets', data),
  updatePermissionSet: (id, data) => api.put(`/rbac/permission-sets/${id}`, data),
  deletePermissionSet: (id) => api.delete(`/rbac/permission-sets/${id}`),
  getUserPermissions: (userId) => api.get(`/rbac/users/${userId}/permissions`),
  assignUserPermissions: (userId, data) => api.put(`/rbac/users/${userId}/permissions`, data),
  getModules: () => api.get('/rbac/modules'),
};

// White-label
export const whitelabelApi = {
  getConfig: () => api.get('/whitelabel/config'),
  updateConfig: (data) => api.put('/whitelabel/config', data),
  uploadLogo: (formData) => api.post('/whitelabel/upload-logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadLoginBg: (formData) => api.post('/whitelabel/upload-login-bg', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// 2FA
export const twoFactorApi = {
  setup: () => api.post('/auth/2fa/setup'),
  verify: (code) => api.post('/auth/2fa/verify', { code }),
  disable: (code) => api.post('/auth/2fa/disable', { code }),
  validate: (userId, code) => api.post('/auth/2fa/validate', { user_id: userId, code }),
  getStatus: () => api.get('/auth/2fa/status'),
};

// Deal Rooms (Capital Raising / Financing / Restructure)
export const dealRoomsApi = {
  getAll: (params) => api.get('/deal-rooms', { params }),
  get: (id) => api.get(`/deal-rooms/${id}`),
  create: (data) => api.post('/deal-rooms', data),
  update: (id, data) => api.put(`/deal-rooms/${id}`, data),
  delete: (id) => api.delete(`/deal-rooms/${id}`),
  getTemplates: () => api.get('/deal-rooms/templates/examples'),
  // Q&A
  getQA: (roomId, params) => api.get(`/deal-rooms/${roomId}/qa`, { params }),
  createQA: (roomId, data) => api.post(`/deal-rooms/${roomId}/qa`, data),
  answerQA: (roomId, qaId, answer) => api.put(`/deal-rooms/${roomId}/qa/${qaId}/answer`, { answer }),
  updateQAStatus: (roomId, qaId, status) => api.patch(`/deal-rooms/${roomId}/qa/${qaId}/status`, { status }),
  deleteQA: (roomId, qaId) => api.delete(`/deal-rooms/${roomId}/qa/${qaId}`),
  getQAStats: (roomId) => api.get(`/deal-rooms/${roomId}/qa/stats`),
};

// User Groups
export const userGroupsApi = {
  getAll: () => api.get('/user-groups'),
  create: (data) => api.post('/user-groups', data),
  update: (id, data) => api.put(`/user-groups/${id}`, data),
  delete: (id) => api.delete(`/user-groups/${id}`),
};

// Document Management (Enhanced)
export const docManagementApi = {
  setPermissions: (docId, data) => api.put(`/documents/${docId}/permissions`, data),
  getPermissions: (docId) => api.get(`/documents/${docId}/permissions`),
  trackActivity: (docId, data) => api.post(`/documents/${docId}/track`, data),
  getActivity: (docId, params) => api.get(`/documents/${docId}/activity`, { params }),
  getRoomAnalytics: (roomId) => api.get(`/data-rooms/${roomId}/activity-analytics`),
  uploadVersion: (docId, formData) => api.post(`/documents/${docId}/versions`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getVersions: (docId) => api.get(`/documents/${docId}/versions`),
};

// Projects
export const projectsApi = {
  getAll: (params) => api.get('/projects', { params }),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  stats: () => api.get('/projects/stats/summary'),
  createMilestone: (projectId, data) => api.post(`/projects/${projectId}/milestones`, data),
  updateMilestone: (projectId, msId, data) => api.put(`/projects/${projectId}/milestones/${msId}`, data),
  deleteMilestone: (projectId, msId) => api.delete(`/projects/${projectId}/milestones/${msId}`),
};

// Supply Chain
export const suppliersApi = {
  getAll: (params) => api.get('/suppliers', { params }),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
};

export const purchaseOrdersApi = {
  getAll: (params) => api.get('/purchase-orders', { params }),
  create: (data) => api.post('/purchase-orders', data),
  update: (id, data) => api.put(`/purchase-orders/${id}`, data),
  delete: (id) => api.delete(`/purchase-orders/${id}`),
};

export const inventoryApi = {
  getAll: (params) => api.get('/inventory', { params }),
  create: (data) => api.post('/inventory', data),
  update: (id, data) => api.put(`/inventory/${id}`, data),
  delete: (id) => api.delete(`/inventory/${id}`),
};

export const supplyChainApi = {
  stats: () => api.get('/supply-chain/stats'),
};

export const biLoungeApi = {
  kpis: () => api.get('/bi/kpis'),
};

// Client Portal
export const clientPortalApi = {
  getAll: () => api.get('/client-portal/invites'),
  create: (data) => api.post('/client-portal/invites', data),
  revoke: (id) => api.delete(`/client-portal/invites/${id}`),
  // Public (no auth)
  view: (token) => api.get(`/client-portal/view/${token}`),
  viewDeal: (token, dealId) => api.get(`/client-portal/view/${token}/deal/${dealId}`),
  viewDocs: (token, dealId) => api.get(`/client-portal/view/${token}/deal/${dealId}/documents`),
  downloadDoc: (token, docId) => api.get(`/client-portal/view/${token}/documents/${docId}/download`, { responseType: 'blob' }),
  postMessage: (token, data) => api.post(`/client-portal/view/${token}/messages`, data),
  getMessages: (token) => api.get(`/client-portal/view/${token}/messages`),
};

export default api;
